import axios from "axios";

/**
	* @typedef {Object} FaxView
	* @property {string} id
	* @property {string} from
	* @property {string} to
	* @property {"inbound" | "outbound"} direction
 	* @property {string} [fileUrl] - signed download URL, only returned by getFax
 	* @property {string} [filename]
	* @property {number} [pageCount]
	* @property {string} status
	* @property {string} requestedAt
	* @property {string} [sentAt]
	* @property {string} [deliveredAt]
	* @property {string} [receivedAt]
	* @property {string} [readAt] - set once viewed/printed/downloaded (inbound)
	*/

/**
	* @typedef {Object} FaxOnboardingView
	* @property {string} id
	* @property {string} organizationId
	* @property {string} phoneNumber
	* @property {boolean} faxEnabled
	* @property {string} connectionId
	* @property {string} createdAt
	* @property {string} updatedAt
	*/

/**
	* @typedef {"inbound" | "outbound"} TelnyxFaxDirection
	*/

const faxService = {
	/**
		* Get organization faxes (server-side search + paging)
		* @param {Object} [params]
		* @param {TelnyxFaxDirection} [params.direction]
		* @param {string} [params.search] - matches document name, to and from numbers
		* @param {number} [params.page] - 1-based page number
		* @param {number} [params.limit] - page size
		* @returns {Promise<{ hits: FaxView[], count: number }>}
		*/
	async listFaxes({ direction, search, page, limit } = {}) {
		const { data } = await axios.get("/faxes", {
			params: {
				direction,
				search: search || undefined,
				page,
				limit
			}
		});

		return data;
	},

	/**
		* Get a fax by id, including a signed download URL
		* @param {string} faxId
		* @returns {Promise<FaxView>}
		*/
	async getFax(faxId) {
		const { data } = await axios.get(`/faxes/${faxId}`);

		return data;
	},

	/**
		* Mark a fax as read (viewed, printed or downloaded)
		* @param {string} faxId
		* @returns {Promise<FaxView>}
		*/
	async markFaxAsRead(faxId) {
		const { data } = await axios.patch(`/faxes/${faxId}/read`);

		return data;
	},

	/**
		* Send fax (the sender number is resolved server-side from onboarding)
		* @param {Object} payload
		* @param {string} payload.to
		* @param {boolean} [payload.deleteFileAfterSend] - delete the document once delivered
		* @param {File | Blob} file
		* @returns {Promise<{ faxId: string, status: string }>}
		*/
	async sendFax(payload, file) {
		const formData = new FormData();

		formData.append("to", payload.to);
		formData.append("deleteFileAfterSend", String(!!payload.deleteFileAfterSend));
		formData.append("file", file);

		const { data } = await axios.post(
			"/faxes/send",
			formData
		);

		return data;
	},

	/**
		* Delete a fax message (record and stored document)
		* @param {string} faxId
		* @returns {Promise<void>}
		*/
	async deleteFax(faxId) {
		await axios.delete(`/faxes/${faxId}`);
	},

	/**
		* Create or update fax onboarding
		* @param {Object} payload
		* @returns {Promise<FaxOnboardingView>}
		*/
	async upsertFaxOnboarding(payload) {
		const { data } = await axios.post(
			"/faxes/onboarding",
			payload
		);

		return data;
	},

	/**
		* Get fax onboarding for current organization
		* @returns {Promise<FaxOnboardingView>}
		*/
	async getFaxOnboardingByOrganization() {
		const { data } = await axios.get(
			"/faxes/onboarding"
		);

		return data;
	},

	/**
		* Get fax onboarding by phone number
		* @param {string} phoneNumber
		* @returns {Promise<FaxOnboardingView>}
		*/
	async getFaxOnboardingByPhone(phoneNumber) {
		const { data } = await axios.get(
			`/faxes/onboarding/by-phone/${phoneNumber}`
		);

		return data;
	},

	/**
		* List fax onboarding records
		* @param {boolean} [faxEnabled]
		* @returns {Promise<FaxOnboardingView[]>}
		*/
	async listFaxOnboarding(faxEnabled) {
		const { data } = await axios.get(
			"/faxes/devops/all",
			{
				params: {
					faxEnabled
				}
			}
		);

		return data;
	}
};

export default faxService;
